import operandType from '../../../constants/functions/operandType';
import { endpointFilterTypeStatementMap } from '../../../constants/report/endpointFilterType';
import conditionHelper from '../condition/conditionHelper';
import locationType from '../../../../types/functions/Location/LocationType';
import Assignment from '../../../../types/functions/Assignment/Assignment';
import Condition from '../../../../types/functions/Condition/Condition';
import Operand from '../../../../types/functions/Operand/Operand';
import Lookup from '../../../../types/functions/Lookup';
import LookupMethod from '../../../../types/report/LookupMethod';
import ReportConfig from '../../../../types/report/ReportConfig';

const errorPrefix = 'Indexed lookup ';

interface ErrorHolder {
    errors: string[];
}

export interface IndexedLookupInvalidComponent {
    statement: string;
    number: number;
    parentNumber?: number;
    name: string;
    kind?: string;
    holder: ErrorHolder;
}

type OperandVisitor = (callback: (operand: Operand) => void) => void;

// Visitors yield only top-level operands; operands nested in `arguments` are not visited.
const visitCondition = (condition: Condition | null | undefined): OperandVisitor =>
    callback => {
        if (!condition) {
            return;
        }

        condition.operand1 && callback(condition.operand1);
        condition.operand2 && callback(condition.operand2);
        visitCondition(condition.and)(callback);
        visitCondition(condition.or)(callback);
    };

const visitAssignment = (assignment: Assignment | null | undefined): OperandVisitor =>
    callback => {
        if (!assignment) {
            return;
        }

        visitCondition(assignment.condition)(callback);
        assignment.value && callback(assignment.value);
        visitAssignment(assignment.else)(callback);
        visitAssignment(assignment.elseIf)(callback);
    };

const visitOperands = (operands: Operand[] | null | undefined): OperandVisitor =>
    callback => (operands || []).forEach(o => o && callback(o));

const combine = (...visitors: OperandVisitor[]): OperandVisitor =>
    callback => visitors.forEach(visit => visit(callback));

const getRequiredArgumentsCount = (lookup: Lookup): number =>
    conditionHelper.asCollection(lookup.condition).filter(c => !!c.operand2?.operandType).length;

const buildMessage = (lookup: Lookup, required: number, args: Operand[]): string | null => {

    if (args.length !== required) {
        return `${errorPrefix}'${lookup.name}' requires ${required} argument(s), but has ${args.length}.`;
    }

    let notSetCount = args.filter(x => !x?.operandType).length;

    if (notSetCount > 0) {
        return `${errorPrefix}'${lookup.name}' requires ${required} argument(s), but ${notSetCount} of them ${notSetCount === 1 ? 'is' : 'are'} not set.`;
    }

    return null;
};

// Removes messages added by a previous run, so fixed references do not keep stale errors.
const removePreviousErrors = (holder: ErrorHolder): void => {
    if (!holder.errors) {
        holder.errors = [];
    }

    for (let i = holder.errors.length - 1; i >= 0; i--) {
        if (holder.errors[i].startsWith(errorPrefix)) {
            holder.errors.splice(i, 1);
        }
    }
};

/**
 * Every reference to an indexed lookup must pass exactly one set argument per lookup key condition with a value.
 * Errors are added to the referencing components; the invalid components are returned.
 */
const validate = (config: ReportConfig): IndexedLookupInvalidComponent[] => {

    const locationStatement = locationType.statement;

    let indexedLookups = new Map<number, { lookup: Lookup, required: number }>();

    (config.lookups || [])
        .filter(x => x.method === LookupMethod.Indexed)
        .forEach(lookup => {
            let required = getRequiredArgumentsCount(lookup);

            if (required > 0) {
                indexedLookups.set(lookup.number, { lookup, required });
            }
        });

    let result: IndexedLookupInvalidComponent[] = [];

    const check = (component: IndexedLookupInvalidComponent, visit: OperandVisitor) => {

        if (!component.holder) {
            return;
        }

        removePreviousErrors(component.holder);

        if (!indexedLookups.size) {
            return;
        }

        let messages: string[] = [];

        visit((operand: Operand) => {

            if (operand?.operandType !== operandType.lookupField) {
                return;
            }

            let target = indexedLookups.get(operand.lookupNumber as number);

            if (!target) {
                return;
            }

            let message = buildMessage(target.lookup, target.required, (operand.arguments || []) as Operand[]);

            if (message && !messages.includes(message)) {
                messages.push(message);
            }
        });

        if (messages.length) {
            messages.forEach(m => component.holder.errors.push(m));
            result.push(component);
        }
    };

    config.variables?.forEach(v => check({ statement: locationStatement.variable, number: v.number, name: v.name, holder: v }, visitAssignment(v.assignment)));

    config.lookups?.filter(x => x.method !== LookupMethod.Indexed).forEach(l => check({ statement: locationStatement.lookup, number: l.number, name: l.name, holder: l }, visitCondition(l.condition)));

    config.userDefinedFunctions?.forEach(f => check({ statement: locationStatement.userDefinedFunction, number: f.number, name: f.name, holder: f }, visitAssignment(f.assignment)));

    config.filters?.forEach(f => check({ statement: locationStatement.filter, number: f.number, name: f.name, holder: f }, visitCondition(f.condition)));

    config.endpointFilters?.forEach(f => check({
        statement: endpointFilterTypeStatementMap[f.type] || locationStatement.endpointFilter, number: f.number, name: f.name, kind: f.type, holder: f
    }, visitCondition(f.condition)));

    config.aggregations?.forEach(a => {
        check({ statement: locationStatement.aggregation, number: a.number, name: a.name, holder: a }, combine(visitCondition(a.condition), visitOperands(a.groupBy)));

        a.fields?.forEach(f => check({
            statement: locationStatement.aggregatedRecordField, number: (f.fieldId || f.variableNumber) as number, parentNumber: a.number, name: a.name, holder: f
        }, visitAssignment(f.assignment)));

        a.filters?.forEach(f => check({
            statement: locationStatement.aggregationFilter, number: f.number, parentNumber: a.number, name: f.name || a.name, holder: f
        }, visitCondition(f.condition)));
    });

    config.cases?.forEach(c => {
        check({ statement: locationStatement.case, number: c.number, name: c.name, holder: c }, visitCondition(c.condition));

        c.fields?.forEach(f => check({
            statement: locationStatement.reportField, number: f.fieldId, parentNumber: c.number, name: c.name, holder: f
        }, visitAssignment(f.assignment)));
    });

    const selectors: [string, any[] | undefined][] = [
        [locationStatement.isinSelector, config.isinSelectors],
        [locationStatement.leiSelector, config.leiSelectors],
        [locationStatement.exchangeRatesSelector, config.exchangeRatesSelectors],
        [locationStatement.annaDsbUpiEnrichmentSelector, config.annaDsbUpiEnrichmentSelectors],
        [locationStatement.annaDsbUpiSelector, config.annaDsbUpiSelectors],
        [locationStatement.lseSelector, config.lseSelectors],
        [locationStatement.symbolSelector, config.symbolSelectors],
        [locationStatement.firdsFcaInstrumentSelector, config.firdsFcaInstrumentSelectors],
        [locationStatement.firdsEsmaInstrumentSelector, config.firdsEsmaInstrumentSelectors],
        [locationStatement.fcaRegulatedEntitiesSelector, config.fcaRegulatedEntitiesSelectors]
    ];

    selectors.forEach(([statement, collection]) => collection?.forEach(s => check(
        { statement, number: s.number, name: s.name, holder: s },
        combine(visitAssignment(s.assignment), ...(s.keys || []).map((k: any) => visitAssignment(k.assignment))))));

    config.validations?.forEach(v => check({ statement: locationStatement.validation, number: v.number, name: v.identifier, kind: v.kind, holder: v }, visitAssignment(v.assignment)));

    if (config.matchingKey) {
        check({ statement: locationStatement.matchingKey, number: 1, name: 'Matching Key', holder: config.matchingKey },
            combine(visitOperands(config.matchingKey.operands), visitAssignment(config.matchingKey.assignment)));
    }

    if (config.matchingKeyEndPoint) {
        check({ statement: locationStatement.matchingKeyEndPoint, number: 2, name: 'Matching Key End Point', holder: config.matchingKeyEndPoint },
            combine(visitOperands(config.matchingKeyEndPoint.operands), visitAssignment(config.matchingKeyEndPoint.assignment)));
    }

    config.dictionaries?.forEach(d => d.fields?.forEach(f => check({
        statement: locationStatement.dictionaryField, number: f.number, parentNumber: d.number, name: f.name, holder: f
    }, visitAssignment(f.assignment))));

    return result;
};

const indexedLookupValidator = {
    validate
};

export default indexedLookupValidator;
